import { ComponentProps, FC, ReactNode } from 'react';
import {
    Button,
    FlexBox,
    Icon,
    Link,
    Typography,
    responsive,
    tokensRaw,
    useScreenClass,
} from '@vp/swan';
import { TranslationKeys, __vistaRoute } from '@99designs/i18n';
import { TenantLogo } from './Logos';

const ResponsiveFlexBox = responsive(FlexBox);

const translateRoute = (path: string) => {
    return __vistaRoute(path as TranslationKeys);
};

const handleHelpClick = (path: string) => {
    // TODO: track metrics
    window.open(translateRoute(path), '_blank');
};

const MinHeightLink: FC<ComponentProps<typeof Link>> = ({ ...props }) => (
    <Link
        style={{
            display: 'flex',
            alignItems: 'center',
            height: 'fit-content',
            padding: tokensRaw.SwanSemSpace2,
        }}
        {...props}
    />
);

export interface TopBarProps {
    logo: TenantLogo;
    logoAria: string;
    help: boolean;
    helpRoute: string;
    helpLabel: string;
    title?: string;
    exit?: ReactNode;
}

function DesktopTopBar({ logo, logoAria, title, exit, help, helpLabel, helpRoute }: TopBarProps) {
    const Logo = logo;

    return (
        <ResponsiveFlexBox
            backgroundColor={'standard'}
            style={{ borderBottom: `1px solid ${tokensRaw.SwanBaseColorGrey300}`, width: '100%' }}
            alignItems="center"
            justifyContent="space-between"
            paddingX={5}
            paddingY={3}
            md={{
                paddingX: 8,
            }}
        >
            <FlexBox alignItems="center" gap={2}>
                <MinHeightLink to="/" aria-label={logoAria}>
                    <Logo />
                </MinHeightLink>
                {title && (
                    <Typography margin={0} fontSkin={'body-standard-bold'}>
                        {title}
                    </Typography>
                )}
            </FlexBox>
            <FlexBox gap={5} paddingY={4}>
                {help && (
                    <Link
                        render={(p) => (
                            <Button
                                skin="tertiary"
                                iconPosition={'left'}
                                className={p.className}
                                onClick={() => handleHelpClick(helpRoute)}
                            >
                                {p.children}
                            </Button>
                        )}
                    >
                        <Icon iconType="helpQuery" /> {helpLabel}
                    </Link>
                )}

                {exit && exit}
            </FlexBox>
        </ResponsiveFlexBox>
    );
}

function MobileTopBar({ logo, logoAria, exit, help, helpRoute }: TopBarProps) {
    const Logo = logo;

    return (
        <FlexBox
            backgroundColor={'standard'}
            style={{
                width: '100%',
                alignContent: 'space-between',
            }}
            alignItems="center"
            justifyContent="space-between"
            paddingX={5}
            paddingY={3}
        >
            {help && (
                <Link
                    to={translateRoute(helpRoute)}
                    render={(p) => (
                        <Button
                            style={{ width: tokensRaw.SwanSemSpace8 }}
                            onClick={() => handleHelpClick(helpRoute)}
                            skin="unstyled"
                            className={p.className}
                        >
                            {p.children}
                        </Button>
                    )}
                >
                    <Icon iconType="helpQuery" />
                </Link>
            )}

            <MinHeightLink to="/" aria-label={logoAria}>
                <Logo />
            </MinHeightLink>

            {exit && exit}
        </FlexBox>
    );
}

export function TopBar({ logo, logoAria, title, exit, help, helpLabel, helpRoute }: TopBarProps) {
    const screenClass = useScreenClass();

    const BarComponent = screenClass === 'xs' ? MobileTopBar : DesktopTopBar;

    return (
        <BarComponent
            exit={exit}
            help={help}
            title={title}
            logo={logo}
            logoAria={logoAria}
            helpLabel={helpLabel}
            helpRoute={helpRoute}
        />
    );
}
